import * as React from 'react';
import { useIntl } from 'react-intl';
import { createColumnHelper, getSortedRowModel } from '@tanstack/react-table';

import { CellBadge, CellDatetime, DataTable, DataTableColumnHeader } from 'src/registry/components/table';
import { subItemQuantityEventMessages } from 'src/features/subscriptionItemQuantityEvents/utils/messages';
import { QUANTITY_EVENT_TYPE_BADGE_OPTIONS } from 'src/features/subscriptionItemQuantityEvents/utils/constants';
import { billingSimulationMessages } from 'src/features/billingSimulation/utils/messages';
import { SimulationQuantityEvent } from 'src/features/billingSimulation/utils/types';

type BillingSimulationQuantityEventsTableProps = {
    events: SimulationQuantityEvent[];
    isLoading?: boolean;
};

const BillingSimulationQuantityEventsTable = ({ events, isLoading }: BillingSimulationQuantityEventsTableProps) => {
    const { formatMessage } = useIntl();

    const columnHelper = React.useMemo(() => createColumnHelper<SimulationQuantityEvent>(), []);

    const columns = React.useMemo(
        () => [
            columnHelper.accessor('recorded_at', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: CellDatetime,
                meta: { title: formatMessage(subItemQuantityEventMessages.recordedAt) },
            }),
            columnHelper.accessor('service_plan_item_name', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => row.original.service_plan_item_name || '-',
                meta: { title: formatMessage(billingSimulationMessages.item) },
            }),
            columnHelper.accessor('event_type', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: CellBadge,
                meta: {
                    title: formatMessage(subItemQuantityEventMessages.eventType),
                    badgeOptions: QUANTITY_EVENT_TYPE_BADGE_OPTIONS,
                },
            }),
            columnHelper.accessor('quantity', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => row.original.quantity,
                meta: { title: formatMessage(subItemQuantityEventMessages.quantity) },
            }),
        ],
        [columnHelper, formatMessage],
    );

    const data = React.useMemo(() => ({ count: events.length, next: null, previous: null, results: events }), [events]);

    return (
        <DataTable
            columns={columns}
            data={data}
            isLoading={isLoading}
            enableSearchParams={false}
            getSortedRowModel={getSortedRowModel()}
            getRowId={(row) => row.id}
            semantics={{
                title: formatMessage(subItemQuantityEventMessages.quantityEvents),
                entryName: formatMessage(subItemQuantityEventMessages.quantityEventTableBadge).toLowerCase(),
                entryNames: formatMessage(subItemQuantityEventMessages.quantityEventsTableBadge).toLowerCase(),
            }}
        />
    );
};

export default BillingSimulationQuantityEventsTable;
